import { useShallow } from 'zustand/react/shallow'

import CoffeeIcon from '@mui/icons-material/Coffee'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import PrintIcon from '@mui/icons-material/Print'
import OtherHousesIcon from '@mui/icons-material/OtherHouses'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import PaletteIcon from '@mui/icons-material/Palette'
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges'
import { Button, IconButton } from '@mui/material'
import dayjs from 'dayjs'
import { memo, useRef, useState } from 'react'
import DateController from '../../components/DateController/DateController'
import UsersConnectedDayView from '../../components/UsersConnected/UsersConnectedDayView'
import { useDayStore } from '../../contexts/DayStore'
import { ROUTE_DAY_VIEW_HEADLESS } from '../../router/Routes'
import { isMobile } from '../../utils/utils'
import { placeBreaks } from '../generator/generatorUtils/break'
import DayViewSettings from './DayViewSettings/DayViewSettings'
import { useDateStore } from '../../contexts/DateStore'
import {
  COLOR_VISION_MG,
  COLOR_VISION_GROUPS,
  useDayViewSettingsStore,
} from '../../contexts/DayViewSettingsStore'
import { checkDay } from '../generator/generatorUtils/checkDay'

const DayViewControls = memo(
  ({
    isHeadLess,
    canEdit,
    canCheck,
    isTablet,
    isFullscreen,
    placeId,
    setPlaceId,
    powerOff,
  }) => {
    const [checkDayRunning, setCheckDayRunning] = useState(false)
    const placeBeforePress = useRef(null)
    const { date } = useDateStore(
      useShallow((state) => ({
        date: state.date,
      }))
    )
    const { day, updateSessionValue } = useDayStore(
      useShallow((state) => ({
        day: state.day,
        updateSessionValue: state.updateSessionValue,
      }))
    )
    const { setColorVision } = useDayViewSettingsStore(
      useShallow((state) => ({
        setColorVision: state.setColorVision,
      }))
    )
    const goToPrint = (place = 1) => {
      window.open(
        `${ROUTE_DAY_VIEW_HEADLESS}?place_id=${place}&date=${date.format('DD-MM-YYYY')}`,
        '_blank',
        'noreferrer'
      )
    }
    const createBreaks = (placeId) => {
      placeBreaks(placeId, day, updateSessionValue)
    }

    const capturePointer = (e) => {
      e.preventDefault()
      e.currentTarget.setPointerCapture?.(e.pointerId)
    }

    const handlePlaceButtonDown = (e) => {
      capturePointer(e)
      placeBeforePress.current = placeId
      setPlaceId(placeId === 1 ? 2 : 1)
    }

    const handlePlaceButtonUp = () => {
      if (placeBeforePress.current !== null) {
        setPlaceId(placeBeforePress.current)
        placeBeforePress.current = null
      }
    }

    const handleColorButtonDown = (e) => {
      capturePointer(e)
      setColorVision(COLOR_VISION_MG)
    }

    const handleColorButtonUp = () => {
      setColorVision(COLOR_VISION_GROUPS)
    }

    const handlePowerButtonDown = (e) => {
      capturePointer(e)
      e.stopPropagation()
      powerOff()
    }

    const handleCheckDayButton = async () => {
      setCheckDayRunning(true)
      await checkDay({ today: day })
      setCheckDayRunning(false)
    }

    const isOnMobile = isMobile()
    return (
      <>
        {canEdit && <UsersConnectedDayView />}
        <div className="headerControls">
          {!isHeadLess && <DateController className="datePicker" />}
          {!isHeadLess && isOnMobile && <DayViewSettings />}
          <div className="title">
            {!isOnMobile && 'Planning '}
            <span className="date">{dayjs(date).format('dddd DD MMMM')}</span>
            {canEdit && <ModeEditIcon className="lockIcon noPrint" />}
          </div>
          {canCheck && (
            <Button
              className="noPrint"
              size="small"
              variant="outlined"
              startIcon={<PublishedWithChangesIcon />}
              disabled={checkDayRunning}
              onClick={handleCheckDayButton}
            >
              Check
            </Button>
          )}
          {canEdit && (
            <>
              <Button
                className="noPrint"
                size="small"
                variant="outlined"
                startIcon={<CoffeeIcon />}
                onClick={() => createBreaks(1)}
              >
                Pauses CLE
              </Button>
              <Button
                className="noPrint"
                size="small"
                variant="outlined"
                startIcon={<CoffeeIcon />}
                onClick={() => createBreaks(2)}
              >
                Pauses SML
              </Button>
            </>
          )}
          {canEdit && (
            <>
              <Button
                className="noPrint"
                size="small"
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => goToPrint(1)}
              >
                CLE
              </Button>
              <Button
                className="noPrint"
                size="small"
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => goToPrint(2)}
              >
                SML
              </Button>
            </>
          )}
          {isHeadLess && !isTablet && !isFullscreen && (
            <IconButton className="triggerPrintBtn noPrint" onClick={() => window.print()}>
              {<PrintIcon />}
            </IconButton>
          )}
          {isHeadLess && isTablet && (
            <>
              <IconButton
                className="triggerOtherLocalBtn noPrint bigButton"
                onPointerDown={handlePlaceButtonDown}
                onPointerUp={handlePlaceButtonUp}
                onPointerCancel={handlePlaceButtonUp}
                onLostPointerCapture={handlePlaceButtonUp}
              >
                {<OtherHousesIcon />}
              </IconButton>
              <IconButton
                className="triggerColorsBtn noPrint bigButton"
                onPointerDown={handleColorButtonDown}
                onPointerUp={handleColorButtonUp}
                onPointerCancel={handleColorButtonUp}
                onLostPointerCapture={handleColorButtonUp}
              >
                {<PaletteIcon />}
              </IconButton>
              <IconButton
                className="triggerPowerBtn noPrint bigButton"
                onPointerDown={handlePowerButtonDown}
              >
                {<PowerSettingsNewIcon />}
              </IconButton>
            </>
          )}
          {!isHeadLess && !isOnMobile && <DayViewSettings className="dayViewSettingDesktop" />}
          {isTablet && <div className="powerOffCover"></div>}
        </div>
      </>
    )
  }
)
export default DayViewControls
